import { useState, useCallback, useMemo } from 'react';
import type { BlogPost } from '@/types/blog';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/auth/use-auth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { QUERY_KEYS } from '@/config/queries';

const INITIAL_LOAD_COUNT = 999; // Load all posts immediately (static content)
const LOAD_MORE_COUNT = 999;

// Blog post IDs for consistent referencing
const BLOG_POST_IDS = [
  'sleep-quality-tips',
  'daily-movement-importance',
  'nutrition-basics',
  'building-healthy-habits',
  'understanding-health-metrics',
  'stress-management-techniques',
  'hydration-and-health',
] as const;

type BlogPostId = (typeof BLOG_POST_IDS)[number];

// Blog content by language
const BLOG_CONTENT: Record<
  string,
  Record<BlogPostId, Omit<BlogPost, 'id' | 'publishedAt' | 'featured'>>
> = {
  en: {
    'sleep-quality-tips': {
      title: '5 Tips for Better Sleep Quality',
      excerpt:
        'Discover proven strategies to improve your sleep hygiene and wake up feeling refreshed and energized.',
      content: `Quality sleep is the foundation of good health and productivity. Here are five evidence-based tips to improve your sleep quality:

**Consistent Sleep Schedule:**
Go to bed and wake up at the same time every day, even on weekends. This helps regulate your body's internal clock.

**Sleep-Friendly Environment:**
Keep your bedroom cool (around 65°F/18°C), dark, and quiet. Consider using blackout curtains and white noise machines.

**Limit Screen Time:**
Blue light from screens can interfere with melatonin production. Avoid screens at least one hour before bedtime.

**Smart Evening Nutrition:**
Avoid large meals, caffeine, and alcohol close to bedtime. A light snack containing tryptophan (like bananas or turkey) can promote sleep.

**Bedtime Routine:**
Create calming pre-sleep rituals like reading, gentle stretching, or meditation to signal to your body that it's time to wind down.

Implementing these habits consistently can significantly improve your sleep quality and overall well-being.`,
      category: 'sleep',
      readingTime: 3,
    },
    'daily-movement-importance': {
      title: 'The Importance of Daily Movement',
      excerpt:
        'Learn why regular physical activity is crucial for both physical and mental health, and how to incorporate it into your routine.',
      content: `Movement is not just about exercise—it's about maintaining the body's natural design for motion. Here's why daily movement matters:

**Physical Health Benefits:**
- Improved cardiovascular health and reduced risk of chronic diseases
- Stronger bones and muscles through regular loading and stress
- Better metabolic function and weight management
- Enhanced immune system function

**Mental Health Benefits:**
- Reduced stress and anxiety through endorphin release
- Improved cognitive function and mental clarity
- Better sleep quality and mood regulation
- Increased self-confidence and body image

**Practical Ways to Add Movement:**
- Take walking meetings instead of sitting
- Use stairs instead of elevators
- Park farther away from entrances
- Stand up and stretch every hour during desk work
- Incorporate short activity breaks throughout the day

Remember, consistency beats intensity. Find activities you enjoy and make them part of your daily routine.`,
      category: 'activity',
      readingTime: 4,
    },
    'nutrition-basics': {
      title: 'Nutrition Basics for Wellness',
      excerpt:
        'Understanding the fundamentals of healthy eating and how to make sustainable dietary choices.',
      content: `Good nutrition provides the building blocks for optimal health. Focus on these core principles:

**Balance Your Plate:**
- Fill half your plate with vegetables and fruits
- One quarter with lean proteins
- One quarter with whole grains
- Include healthy fats in moderation

**Key Nutrients to Prioritize:**
- Protein: Essential for muscle repair and immune function
- Fiber: Supports digestive health and blood sugar control
- Healthy Fats: Important for brain health and hormone production
- Micronutrients: Vitamins and minerals from diverse food sources

**Sustainable Eating Habits:**
- Eat the rainbow: Different colored fruits and vegetables provide different nutrients
- Stay hydrated: Water is essential for all bodily functions
- Mindful eating: Pay attention to hunger and fullness cues
- Meal prep: Plan ahead to make healthy choices easier

Focus on nourishing your body rather than restrictive dieting. Small, consistent changes lead to lasting results.`,
      category: 'nutrition',
      readingTime: 3,
    },
    'building-healthy-habits': {
      title: 'Building Healthy Habits',
      excerpt:
        'Learn the science behind habit formation and practical strategies to create lasting positive changes.',
      content: `Creating lasting healthy habits requires understanding how behavior change works. Here's what science tells us:

**The Habit Loop:**
Every habit consists of three parts:
- Cue: The trigger that initiates the behavior
- Routine: The behavior itself
- Reward: The benefit you get from the behavior

**Strategies for Success:**
- Start small: Focus on tiny changes that are easy to maintain
- Stack habits: Attach new habits to existing routines
- Track progress: Use a habit tracker or journal
- Be patient: Real change takes time and consistency

**Common Pitfalls to Avoid:**
- Trying to change too much at once
- Being too rigid or perfectionistic
- Not having accountability
- Giving up after setbacks

**Building Momentum:**
- Celebrate small wins
- Find an accountability partner
- Create environmental cues for good habits
- Focus on identity change, not just behavior change

Remember, habits are built through repetition, not motivation. Small daily actions compound into significant results over time.`,
      category: 'wellness',
      readingTime: 4,
    },
    'understanding-health-metrics': {
      title: 'Understanding Your Health Metrics',
      excerpt:
        'Learn which health indicators matter most and how to interpret common biometric measurements.',
      content: `Health metrics provide valuable insights into your well-being. Understanding them empowers better health decisions.

**Key Vital Signs:**
- **Blood Pressure**: Normal is less than 120/80 mmHg
- **Heart Rate**: Resting should be 60-100 bpm
- **Body Temperature**: Normal range is 97-99°F (36.1-37.2°C)
- **Respiratory Rate**: Normal breathing is 12-20 breaths per minute

**Important Biomarkers:**
- **Cholesterol**: HDL should be >40 mg/dL, LDL <100 mg/dL
- **Blood Sugar**: Fasting glucose should be <100 mg/dL
- **BMI**: Healthy range is 18.5-24.9
- **Body Fat Percentage**: Varies by age and gender

**Wearable Device Metrics:**
- **Steps**: Aim for 7,000-10,000 daily
- **Active Minutes**: 150 minutes of moderate activity weekly
- **Sleep Duration**: 7-9 hours for adults
- **Heart Rate Variability**: Higher is generally better

Regular monitoring helps you make informed decisions about your lifestyle and healthcare.`,
      category: 'wellness',
      readingTime: 4,
    },
    'stress-management-techniques': {
      title: 'Effective Stress Management Techniques',
      excerpt:
        'Discover practical tools and strategies to manage stress and maintain mental well-being in demanding times.',
      content: `Stress is a natural response to challenges, but chronic stress can harm your health. Here are effective management techniques:

**Breathing Exercises:**
- 4-7-8 breathing: Inhale for 4 counts, hold for 7, exhale for 8
- Box breathing: Inhale, hold, exhale, hold - each for 4 counts
- Deep belly breathing: Focus on expanding your diaphragm

**Mindfulness Practices:**
- Meditation: Start with 5 minutes daily
- Body scan: Systematically relax different muscle groups
- Mindful walking: Focus on each step and sensation

**Physical Stress Relief:**
- Progressive muscle relaxation
- Yoga or tai chi
- Regular exercise and movement
- Massage or self-massage techniques

**Cognitive Strategies:**
- Challenge negative thought patterns
- Practice gratitude journaling
- Use positive affirmations
- Reframe stressful situations

Remember, stress management is personal. Experiment with different techniques to find what works best for you.`,
      category: 'wellness',
      readingTime: 3,
    },
    'hydration-and-health': {
      title: 'The Role of Hydration in Health',
      excerpt:
        'Understanding why proper hydration is essential and how to maintain optimal fluid balance.',
      content: `Water is fundamental to every aspect of health. Proper hydration supports all bodily functions.

**Why Hydration Matters:**
- Regulates body temperature
- Transports nutrients and oxygen
- Removes waste products
- Maintains electrolyte balance
- Supports joint health
- Aids digestion

**Signs of Dehydration:**
- Thirst
- Dry mouth and skin
- Dark urine
- Fatigue
- Headaches
- Dizziness

**Daily Water Needs:**
- General guideline: 3.7 liters for men, 2.7 liters for women
- Adjust for activity level, climate, and health conditions
- Eat water-rich foods (fruits and vegetables)

**Hydration Strategies:**
- Carry a reusable water bottle
- Set reminders to drink regularly
- Drink before, during, and after exercise
- Monitor urine color as a hydration indicator

Making hydration a habit is one of the simplest ways to support your overall health.`,
      category: 'nutrition',
      readingTime: 3,
    },
  },
  de: {
    'sleep-quality-tips': {
      title: '5 Tipps für bessere Schlafqualität',
      excerpt:
        'Entdecke bewährte Strategien, um deine Schlafhygiene zu verbessern und erfrischt und energiegeladen aufzuwachen.',
      content: `Qualitativ hochwertiger Schlaf ist die Grundlage für gute Gesundheit und Produktivität. Hier sind fünf evidenzbasierte Tipps zur Verbesserung deiner Schlafqualität:

**Konstanter Schlafrhythmus:**
Gehe jeden Tag zur gleichen Zeit ins Bett und stehe auf, auch am Wochenende. Dies hilft, deine innere Uhr zu regulieren.

**Schlaffreundliche Umgebung:**
Halte dein Schlafzimmer kühl (ca. 18°C), dunkel und ruhig. Verdunklungsvorhänge und White-Noise-Geräte können helfen.

**Bildschirmzeit begrenzen:**
Blaues Licht von Bildschirmen kann die Melatoninproduktion stören. Vermeide Bildschirme mindestens eine Stunde vor dem Schlafengehen.

**Kluge Abendernährung:**
Vermeide große Mahlzeiten, Koffein und Alkohol kurz vor dem Schlafengehen. Ein leichter Snack mit Tryptophan (wie Bananen oder Truthahn) kann den Schlaf fördern.

**Abendroutine:**
Schaffe beruhigende Vor-Schlaf-Rituale wie Lesen, sanftes Dehnen oder Meditation, um deinem Körper zu signalisieren, dass es Zeit ist, herunterzufahren.

Die konsequente Umsetzung dieser Gewohnheiten kann deine Schlafqualität und dein allgemeines Wohlbefinden deutlich verbessern.`,
      category: 'sleep',
      readingTime: 3,
    },
    'daily-movement-importance': {
      title: 'Die Bedeutung täglicher Bewegung',
      excerpt:
        'Erfahre, warum regelmäßige körperliche Aktivität für körperliche und geistige Gesundheit entscheidend ist.',
      content: `Bewegung ist nicht nur Sport – es geht darum, das natürliche Design des Körpers für Bewegung zu erhalten. Warum tägliche Bewegung wichtig ist:

**Körperliche Gesundheitsvorteile:**
- Verbesserte Herz-Kreislauf-Gesundheit und reduziertes Risiko für chronische Krankheiten
- Stärkere Knochen und Muskeln durch regelmäßige Belastung
- Bessere Stoffwechselfunktion und Gewichtsmanagement
- Verbessertes Immunsystem

**Mentale Gesundheitsvorteile:**
- Reduzierter Stress und Angst durch Endorphinausschüttung
- Verbesserte kognitive Funktion und mentale Klarheit
- Bessere Schlafqualität und Stimmungsregulation
- Gesteigertes Selbstvertrauen

**Praktische Wege, mehr Bewegung einzubauen:**
- Walking-Meetings statt Sitzen
- Treppen statt Aufzüge nutzen
- Weiter weg parken
- Jede Stunde aufstehen und dehnen
- Kurze Aktivitätspausen über den Tag verteilt

Denke daran: Konsistenz schlägt Intensität. Finde Aktivitäten, die dir Spaß machen.`,
      category: 'activity',
      readingTime: 4,
    },
    'nutrition-basics': {
      title: 'Ernährungsgrundlagen für Wohlbefinden',
      excerpt:
        'Verstehe die Grundlagen gesunder Ernährung und wie du nachhaltige Ernährungsentscheidungen triffst.',
      content: `Gute Ernährung liefert die Bausteine für optimale Gesundheit. Konzentriere dich auf diese Kernprinzipien:

**Ausgewogener Teller:**
- Fülle die Hälfte deines Tellers mit Gemüse und Obst
- Ein Viertel mit magerem Eiweiß
- Ein Viertel mit Vollkornprodukten
- Gesunde Fette in Maßen

**Wichtige Nährstoffe:**
- Protein: Essentiell für Muskelreparatur und Immunfunktion
- Ballaststoffe: Unterstützen Verdauung und Blutzuckerkontrolle
- Gesunde Fette: Wichtig für Gehirngesundheit und Hormonproduktion
- Mikronährstoffe: Vitamine und Mineralstoffe aus vielfältigen Quellen

**Nachhaltige Essgewohnheiten:**
- Iss den Regenbogen: Verschiedenfarbiges Obst und Gemüse liefert verschiedene Nährstoffe
- Bleib hydriert: Wasser ist essentiell für alle Körperfunktionen
- Achtsames Essen: Achte auf Hunger- und Sättigungssignale
- Meal Prep: Plane voraus, um gesunde Entscheidungen einfacher zu machen

Konzentriere dich auf Nährung statt restriktive Diäten. Kleine, konstante Änderungen führen zu dauerhaften Ergebnissen.`,
      category: 'nutrition',
      readingTime: 3,
    },
    'building-healthy-habits': {
      title: 'Gesunde Gewohnheiten aufbauen',
      excerpt:
        'Lerne die Wissenschaft hinter Gewohnheitsbildung und praktische Strategien für dauerhafte positive Veränderungen.',
      content: `Dauerhafte gesunde Gewohnheiten zu schaffen erfordert Verständnis, wie Verhaltensänderung funktioniert:

**Die Gewohnheitsschleife:**
Jede Gewohnheit besteht aus drei Teilen:
- Auslöser: Der Trigger, der das Verhalten initiiert
- Routine: Das Verhalten selbst
- Belohnung: Der Nutzen, den du aus dem Verhalten ziehst

**Strategien für Erfolg:**
- Klein anfangen: Fokussiere auf winzige Änderungen, die leicht beizubehalten sind
- Gewohnheiten stapeln: Verknüpfe neue Gewohnheiten mit bestehenden Routinen
- Fortschritt verfolgen: Nutze einen Gewohnheitstracker oder Journal
- Geduldig sein: Echte Veränderung braucht Zeit und Konsistenz

**Häufige Fallstricke vermeiden:**
- Zu viel auf einmal ändern wollen
- Zu starr oder perfektionistisch sein
- Keine Verantwortlichkeit haben
- Nach Rückschlägen aufgeben

**Momentum aufbauen:**
- Kleine Erfolge feiern
- Einen Accountability-Partner finden
- Umgebungsreize für gute Gewohnheiten schaffen
- Auf Identitätswandel fokussieren, nicht nur auf Verhaltensänderung

Gewohnheiten werden durch Wiederholung aufgebaut, nicht durch Motivation.`,
      category: 'wellness',
      readingTime: 4,
    },
    'understanding-health-metrics': {
      title: 'Deine Gesundheitsmetriken verstehen',
      excerpt:
        'Erfahre, welche Gesundheitsindikatoren am wichtigsten sind und wie du biometrische Messungen interpretierst.',
      content: `Gesundheitsmetriken liefern wertvolle Einblicke in dein Wohlbefinden. Sie zu verstehen ermöglicht bessere Gesundheitsentscheidungen.

**Wichtige Vitalzeichen:**
- **Blutdruck**: Normal ist unter 120/80 mmHg
- **Herzfrequenz**: Ruhepuls sollte 60-100 bpm sein
- **Körpertemperatur**: Normalbereich ist 36,1-37,2°C
- **Atemfrequenz**: Normales Atmen ist 12-20 Atemzüge pro Minute

**Wichtige Biomarker:**
- **Cholesterin**: HDL sollte >40 mg/dL sein, LDL <100 mg/dL
- **Blutzucker**: Nüchternglukose sollte <100 mg/dL sein
- **BMI**: Gesunder Bereich ist 18,5-24,9
- **Körperfettanteil**: Variiert nach Alter und Geschlecht

**Wearable-Gerät-Metriken:**
- **Schritte**: Ziel sind 7.000-10.000 täglich
- **Aktive Minuten**: 150 Minuten moderate Aktivität wöchentlich
- **Schlafdauer**: 7-9 Stunden für Erwachsene
- **Herzratenvariabilität**: Höher ist generell besser

Regelmäßiges Monitoring hilft dir, informierte Entscheidungen über deinen Lebensstil zu treffen.`,
      category: 'wellness',
      readingTime: 4,
    },
    'stress-management-techniques': {
      title: 'Effektive Stressmanagement-Techniken',
      excerpt:
        'Entdecke praktische Werkzeuge und Strategien, um Stress zu bewältigen und mentales Wohlbefinden zu erhalten.',
      content: `Stress ist eine natürliche Reaktion auf Herausforderungen, aber chronischer Stress kann deiner Gesundheit schaden. Hier sind effektive Bewältigungstechniken:

**Atemübungen:**
- 4-7-8 Atmung: 4 Sekunden einatmen, 7 halten, 8 ausatmen
- Box-Atmung: Einatmen, halten, ausatmen, halten – jeweils 4 Sekunden
- Tiefe Bauchatmung: Fokus auf Zwerchfellausdehnung

**Achtsamkeitspraktiken:**
- Meditation: Beginne mit 5 Minuten täglich
- Body Scan: Systematisch verschiedene Muskelgruppen entspannen
- Achtsames Gehen: Fokussiere auf jeden Schritt und jede Empfindung

**Körperliche Stressentlastung:**
- Progressive Muskelentspannung
- Yoga oder Tai Chi
- Regelmäßige Bewegung
- Massage oder Selbstmassage-Techniken

**Kognitive Strategien:**
- Negative Gedankenmuster hinterfragen
- Dankbarkeitstagebuch führen
- Positive Affirmationen nutzen
- Stressige Situationen umdeuten

Stressmanagement ist persönlich. Experimentiere mit verschiedenen Techniken, um herauszufinden, was für dich am besten funktioniert.`,
      category: 'wellness',
      readingTime: 3,
    },
    'hydration-and-health': {
      title: 'Die Rolle von Hydration für die Gesundheit',
      excerpt:
        'Verstehe, warum richtige Hydration essentiell ist und wie du einen optimalen Flüssigkeitshaushalt aufrechterhältst.',
      content: `Wasser ist fundamental für jeden Aspekt der Gesundheit. Richtige Hydration unterstützt alle Körperfunktionen.

**Warum Hydration wichtig ist:**
- Reguliert die Körpertemperatur
- Transportiert Nährstoffe und Sauerstoff
- Entfernt Abfallprodukte
- Erhält das Elektrolytgleichgewicht
- Unterstützt die Gelenkgesundheit
- Fördert die Verdauung

**Anzeichen von Dehydration:**
- Durst
- Trockener Mund und Haut
- Dunkler Urin
- Müdigkeit
- Kopfschmerzen
- Schwindel

**Täglicher Wasserbedarf:**
- Allgemeine Richtlinie: 3,7 Liter für Männer, 2,7 Liter für Frauen
- Anpassen je nach Aktivitätslevel, Klima und Gesundheitszustand
- Wasserreiche Lebensmittel essen (Obst und Gemüse)

**Hydrations-Strategien:**
- Wiederverwendbare Wasserflasche mitnehmen
- Erinnerungen setzen, regelmäßig zu trinken
- Vor, während und nach dem Training trinken
- Urinfarbe als Hydrations-Indikator überwachen

Hydration zur Gewohnheit zu machen ist eine der einfachsten Wege, deine Gesundheit zu unterstützen.`,
      category: 'nutrition',
      readingTime: 3,
    },
  },
};

// Static metadata for posts
const POST_METADATA: Record<BlogPostId, { publishedAt: string; featured: boolean }> = {
  'sleep-quality-tips': { publishedAt: '2024-11-20T08:00:00Z', featured: true },
  'daily-movement-importance': { publishedAt: '2024-11-18T10:30:00Z', featured: false },
  'nutrition-basics': { publishedAt: '2024-11-15T14:20:00Z', featured: false },
  'building-healthy-habits': { publishedAt: '2024-11-12T09:15:00Z', featured: false },
  'understanding-health-metrics': { publishedAt: '2024-11-10T11:45:00Z', featured: false },
  'stress-management-techniques': { publishedAt: '2024-11-08T13:30:00Z', featured: false },
  'hydration-and-health': { publishedAt: '2024-11-05T16:00:00Z', featured: false },
};

function getIsoWeekStart(date = new Date()): Date {
  const copy = new Date(date);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function buildWeeklyInsightPost(params: {
  language: string;
  weekStart: Date;
  steps: number | null | undefined;
  sleepHours: number | null | undefined;
  activeMinutes: number | null | undefined;
  energyLevel: number | null | undefined;
}): BlogPost {
  const { language, weekStart, steps, sleepHours, activeMinutes, energyLevel } = params;
  const weekLabel = weekStart.toLocaleDateString(language, { month: 'short', day: 'numeric' });
  const avgSteps = Math.max(0, Math.round(steps ?? 0));
  const avgSleep = Math.max(0, Number((sleepHours ?? 0).toFixed(1)));
  const avgActive = Math.max(0, Math.round(activeMinutes ?? 0));
  const energy = energyLevel ?? null;

  const isGerman = language.startsWith('de');
  const title = isGerman
    ? `Dein Wochen-Insight (${weekLabel})`
    : `Your Weekly Insight (${weekLabel})`;
  const excerpt = isGerman
    ? 'Automatisch aus deinen aktuellen Gesundheitsdaten erstellt, mit Fokus auf die kommende Woche.'
    : 'Automatically generated from your latest health data, focused on next-week actions.';

  const content = isGerman
    ? `So sah deine letzte Woche aus:\n\n- **Ø Schritte:** ${avgSteps.toLocaleString('de-DE')}\n- **Ø Schlaf:** ${avgSleep} h\n- **Ø Aktive Minuten:** ${avgActive}\n${energy !== null ? `- **Energie (Check-in):** ${energy}/10\n` : ''}\n\nDein Fokus für diese Woche:\n\n1. Halte täglich mindestens **${Math.max(7000, Math.round(avgSteps * 0.95)).toLocaleString('de-DE')} Schritte**.\n2. Plane eine feste Schlafzeit, um auf **mindestens ${Math.max(7, avgSleep).toFixed(1)} h** zu kommen.\n3. Setze **2 kurze Bewegungsslots** (je 15 Minuten) an trainingsfreien Tagen.\n\nDiese Wochen-Notiz wird automatisch jede Woche aktualisiert.`
    : `This is what your last week looked like:\n\n- **Avg steps:** ${avgSteps.toLocaleString('en-US')}\n- **Avg sleep:** ${avgSleep} h\n- **Avg active minutes:** ${avgActive}\n${energy !== null ? `- **Energy (check-in):** ${energy}/10\n` : ''}\n\nFocus for this week:\n\n1. Keep at least **${Math.max(7000, Math.round(avgSteps * 0.95)).toLocaleString('en-US')} steps** daily.\n2. Lock a consistent bedtime to reach **at least ${Math.max(7, avgSleep).toFixed(1)} h** sleep.\n3. Add **2 short movement slots** (15 min each) on non-training days.\n\nThis weekly note is refreshed automatically every week.`;

  return {
    id: `weekly-insight-${weekStart.toISOString().split('T')[0]}`,
    title,
    excerpt,
    content,
    category: 'wellness',
    readingTime: 2,
    publishedAt: weekStart.toISOString(),
    featured: true,
  };
}

export function useBlog() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [loadedCount, setLoadedCount] = useState(INITIAL_LOAD_COUNT);

  // Get content for current language, fallback to English
  const currentLang = i18n.language?.substring(0, 2) || 'en';
  const langContent = BLOG_CONTENT[currentLang] || BLOG_CONTENT.en;
  const weekStart = getIsoWeekStart();
  const weekStartIso = weekStart.toISOString().split('T')[0];

  const { data: weeklyInsightPost } = useQuery({
    queryKey: user?.id
      ? QUERY_KEYS.COACH_WEEKLY_BLOG_INSIGHT(user.id, weekStartIso, currentLang)
      : QUERY_KEYS.COACH_WEEKLY_BLOG_INSIGHT('anonymous', weekStartIso, currentLang),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 30,
    queryFn: async (): Promise<BlogPost | null> => {
      if (!user?.id) return null;

      const [healthRowRes, checkinRes] = await Promise.all([
        supabase
          .from('user_health_data')
          .select('steps, sleep_hours, active_minutes')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('weekly_checkins')
          .select('energy_level')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (healthRowRes.error && checkinRes.error) {
        return null;
      }

      return buildWeeklyInsightPost({
        language: currentLang,
        weekStart,
        steps: healthRowRes.data?.steps ?? null,
        sleepHours: healthRowRes.data?.sleep_hours ?? null,
        activeMinutes: healthRowRes.data?.active_minutes ?? null,
        energyLevel: checkinRes.data?.energy_level ?? null,
      });
    },
  });

  // Build posts array based on current language
  const allPosts = useMemo((): BlogPost[] => {
    const localizedPosts = BLOG_POST_IDS.map((id) => ({
      id,
      ...langContent[id],
      ...POST_METADATA[id],
    }));

    if (!weeklyInsightPost) return localizedPosts;
    return [weeklyInsightPost, ...localizedPosts.map((post) => ({ ...post, featured: false }))];
  }, [langContent, weeklyInsightPost]);

  // Slice posts based on loaded count for progressive loading
  const posts = useMemo(() => allPosts.slice(0, loadedCount), [allPosts, loadedCount]);
  const hasMore = loadedCount < allPosts.length;

  const loadMore = useCallback(() => {
    if (hasMore) {
      setLoadedCount((prev) => Math.min(prev + LOAD_MORE_COUNT, allPosts.length));
    }
  }, [hasMore, allPosts.length]);

  const prefetchNext = useCallback(() => {
    // No-op for static content
  }, []);

  const refreshPosts = useCallback(() => {
    // Static content doesn't need refreshing
  }, []);

  return {
    posts,
    isLoading: false, // Static data loads instantly
    isLoadingMore: false, // No async loading
    hasMore,
    loadMore,
    prefetchNext,
    refreshPosts,
    error: null,
    isRefreshing: false,
  };
}
